/**
 * /notifications — user's inbox of alerts + pending connection requests.
 * Pending requests show Accept / Decline buttons inline.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, UserPlus, Check, X, Loader2, MessageCircle, Heart, Cloud } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";

const KIND_ICON = {
  connection_request: UserPlus,
  connection_accepted: Check,
  message: MessageCircle,
  weather_alert: Cloud,
};

export default function Notifications() {
  const { user } = useAuth();
  const [data, setData] = useState({ notifications: [], pending_requests: [], unread: 0 });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    http.get("/notifications/me").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (user) load(); }, [user]);

  const markAllRead = async () => {
    await http.post("/notifications/read-all");
    load();
  };

  const accept = async (requestId) => {
    try { await http.post(`/connections/accept/${requestId}`); toast.success("Connected!"); load(); }
    catch (e) { toast.error("Couldn't accept."); }
  };
  const decline = async (requestId) => {
    try { await http.post(`/connections/decline/${requestId}`); load(); }
    catch (e) { toast.error("Couldn't decline."); }
  };

  if (!user) {
    return <div className="max-w-4xl mx-auto px-6 py-16 text-center" style={{ color: "var(--wofy-text-soft)" }}>Please sign in to view your notifications.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl">Notifications</h1>
          {data.unread > 0 && <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>{data.unread} unread</p>}
        </div>
        <button onClick={markAllRead} className="wofy-btn-ghost text-sm" data-testid="notifications-mark-all">Mark all read</button>
      </div>

      {/* Pending connection requests */}
      {data.pending_requests?.length > 0 && (
        <section className="mb-8">
          <h2 className="overline mb-3">Pending connection requests</h2>
          <div className="space-y-3">
            {data.pending_requests.map((r) => (
              <div key={r.id} className="wofy-card !p-4 flex items-center gap-3" data-testid={`req-${r.id}`}>
                <Avatar src={r.from_avatar} name={r.from_name} gender={r.from_gender} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <Link to={r.from_handle ? `/u/${r.from_handle}` : "#"} className="font-medium hover:underline">{r.from_name || "Someone"}</Link>
                    <span className="opacity-60"> wants to connect</span>
                  </div>
                  {r.message && <div className="text-xs mt-1 italic" style={{ color: "var(--wofy-text-soft)" }}>"{r.message}"</div>}
                </div>
                <button onClick={() => accept(r.id)} className="wofy-btn-primary text-xs" data-testid={`req-accept-${r.id}`}>
                  <Check className="w-3.5 h-3.5" /> Accept
                </button>
                <button onClick={() => decline(r.id)} className="wofy-btn-ghost text-xs" data-testid={`req-decline-${r.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {loading ? (
        <div className="text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : data.notifications.length === 0 && data.pending_requests.length === 0 ? (
        <div className="wofy-card text-center py-12" style={{ color: "var(--wofy-text-soft)" }}>
          <Bell className="w-8 h-8 mx-auto mb-3 opacity-40" />
          No notifications yet. Weather alerts, connection requests, and messages will appear here.
        </div>
      ) : (
        <section>
          <h2 className="overline mb-3">Recent activity</h2>
          <div className="space-y-2">
            {data.notifications.map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              const unread = !n.read_at;
              return (
                <div
                  key={n.id}
                  className="wofy-card !p-3 flex items-start gap-3"
                  style={unread ? { background: "#eff6ff" } : {}}
                  data-testid={`note-${n.id}`}
                >
                  <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--wofy-primary)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{n.headline}</div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>{n.message}</div>
                    <div className="text-[10px] font-mono mt-1 opacity-60">{new Date(n.created_at).toLocaleString()}</div>
                    {n.meta?.from_handle && (
                      <Link to={`/messages?to=${n.meta.from_user_id}`} className="text-xs underline mt-1 inline-block" style={{ color: "var(--wofy-primary)" }}>
                        Open conversation →
                      </Link>
                    )}
                  </div>
                  {unread && <span className="w-2 h-2 rounded-full mt-2" style={{ background: "var(--wofy-primary)" }} />}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
